import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Card,
  Collapse,
  Container,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ArrowDownward,
  ArrowOutward,
  ChevronLeft,
  ChevronRight,
  Close,
  ExpandLess,
  ExpandMore,
  LocalBar,
  Menu as MenuIcon,
  Restaurant,
  Share,
  WhatsApp,
  Fastfood,
  BrunchDining,
  Coffee,
  LocalPizza,
  Cake,
  SoupKitchen,
  RiceBowl,
  WineBar,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccessTime as AccessTimeIcon,
  MailOutline as MailOutlineIcon,
  Instagram,
  Email,
  LocationOn,
  Schedule,
} from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";

/* -------------------------------- ASSETS -------------------------------- */
/* Place these files inside: src/assets/  (adjust the paths if yours differ) */
import logo from "../assets/logo.jpg";
import slide1 from "../assets/1.jpeg";
import slide2 from "../assets/2.jpeg";
import slide3 from "../assets/3.png";
import slide4 from "../assets/4.jpg";
import slide5 from "../assets/5.jpeg";

/* ------------------------------------------------------------------ */
/*  CONSTANTS                                                          */
/* ------------------------------------------------------------------ */

const ACCENT = "#d7a66d";
const INK = "#171512";
const MUTED = "#a9a39a";
const HAIRLINE = "rgba(255,255,255,.08)";

const CONTACT = {
  phone: "(+234) 704 100 5087",
  phoneHref: "+2347041005087",
  address: "4th Floor, 1 Bria St, Adetokunbo Ademola Cres, Wuse 2, Abuja",
  email: "info@myelegante.com",
  hours: [{ label: "Mon – Sun", value: "3pm – 2am daily" }],
  socials: {
    instagram:
      "https://www.instagram.com/theterraceabuja?exln=MW9ta3U0djh5ODVhcA%3D%3D&utm_source=qr",
    tiktok: "https://www.tiktok.com/@theterraceabuja?_r=1&_t=ZS-9AnK5AhgUTg",
  },
};

const slides = [
  {
    eyebrow: "AFTER DARK • ABUJA",
    title: "Where the night\ncomes alive.",
    subtitle: "Signature pours, deep bass and a crowd that knows how to move.",
    image: slide1,
  },
  {
    eyebrow: "COCKTAILS • MUSIC • GOOD COMPANY",
    title: "Sip slow.\nStay late.",
    subtitle: "Craft cocktails and late-night plates served until the city sleeps.",
    image: slide2,
  },
  {
    eyebrow: "YOUR TABLE IS WAITING",
    title: "One more round.\nOne more memory.",
    subtitle: "From sunset to 2am — the Terrace Lounge is where the fun begins.",
    image: slide3,
  },
  {
    eyebrow: "PLATES • POURS • PEOPLE",
    title: "Made for\nlong nights.",
    subtitle: "Warm light, good food and a room that never rushes you out.",
    image: slide4,
  },
  {
    eyebrow: "THE TERRACE • WUSE 2",
    title: "Come as you are.\nStay as late as you like.",
    subtitle: "Your table, your people, your kind of evening.",
    image: slide5,
  },
];

const categoryIcons = {
  "Local Main Course": <RiceBowl />,
  "Continental Main Course": <Restaurant />,
  Drinks: <LocalBar />,
  Shawarma: <Fastfood />,
  Pizza: <LocalPizza />,
  Platters: <BrunchDining />,
  "Charcoal Grill": <Restaurant />,
  "Local Starters": <SoupKitchen />,
  "Pepper Soup": <SoupKitchen />,
  Dessert: <Cake />,
  Burger: <Fastfood />,
  "Continental Starters": <SoupKitchen />,
  Salad: <BrunchDining />,
  Pasta: <Restaurant />,
};

const subcategoryIcons = {
  Starters: <SoupKitchen />,
  "Main Dishes": <Restaurant />,
  Desserts: <Cake />,
  "Cold Drinks": <LocalBar />,
  "Hot Drinks": <Coffee />,
  Pizza: <LocalPizza />,
  Pasta: <Restaurant />,
  Salads: <BrunchDining />,
  Rice: <RiceBowl />,
  Snacks: <Fastfood />,
  "Signature Cocktails": <WineBar />,
  Mocktails: <LocalBar />,
};

// TikTok icon (MUI doesn't ship one) — simple inline SVG
const TikTokIcon = ({ sx }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    sx={{ fontSize: 18, ...sx }}
  >
    <path d="M16.6 5.82a4.28 4.28 0 0 1-3.13-1.36 4.28 4.28 0 0 1-1.16-2.96h-3.1v13.2a2.55 2.55 0 1 1-1.82-2.44V9.1a5.63 5.63 0 1 0 4.92 5.58V8.86a7.3 7.3 0 0 0 4.29 1.38V7.14a4.3 4.3 0 0 1 0-1.32z" />
  </Box>
);

const socialMeta = {
  instagram: { label: "Instagram", icon: <Instagram sx={{ fontSize: 18 }} /> },
  tiktok: { label: "TikTok", icon: <TikTokIcon /> },
};

/* ------------------------------------------------------------------ */
/*  HELPERS                                                            */
/* ------------------------------------------------------------------ */

function getCategoryIcon(name) {
  return categoryIcons[String(name ?? "").trim()] || <Restaurant />;
}

function getSubcategoryIcon(name) {
  return subcategoryIcons[String(name ?? "").trim()] || <Restaurant />;
}

const normalise = (value) => String(value ?? "").trim().toLowerCase();

function cleanMenuData(data) {
  if (!Array.isArray(data)) return [];

  return data
    .map((category) => {
      const directItems = [...(category.items || [])];
      const subcategories = [];

      (category.subcategories || []).forEach((sub) => {
        if (normalise(sub.name) === normalise(category.name)) {
          directItems.push(...(sub.items || []));
        } else if ((sub.items || []).length) {
          subcategories.push({ ...sub, items: [...sub.items] });
        }
      });

      return { ...category, items: directItems, subcategories };
    })
    .filter((category) => category.items.length || category.subcategories.length)
    .sort((a, b) => {
      const orderA = a.sort_order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.sort_order ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB || (a.id ?? 0) - (b.id ?? 0);
    });
}

function formatPrice(price) {
  if (price === null || price === undefined || price === "") return "Ask for price";
  const amount = Number(price);
  if (!Number.isFinite(amount) || amount <= 0) return "Ask for price";
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(amount);
}

function normaliseHours(hours) {
  if (!hours) return CONTACT.hours;

  const list = Array.isArray(hours)
    ? hours
    : String(hours)
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

  if (!list.length) return CONTACT.hours;

  return list.map((entry) => {
    if (entry && typeof entry === "object") {
      return {
        label: String(entry.label ?? entry.day ?? "").trim(),
        value: String(entry.value ?? entry.time ?? entry.hours ?? "").trim(),
      };
    }
    const str = String(entry);
    const colonIdx = str.indexOf(":");
    if (colonIdx === -1) return { label: str.trim(), value: "" };
    return {
      label: str.slice(0, colonIdx).trim(),
      value: str.slice(colonIdx + 1).trim(),
    };
  });
}

const infoChipSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  borderRadius: "8px",
  background: "rgba(215,166,109,.12)",
  border: "1px solid rgba(215,166,109,.3)",
  flexShrink: 0,
};

const infoLabelSx = {
  fontSize: ".58rem",
  letterSpacing: ".15em",
  color: "#a9a39a",
  lineHeight: 1,
};

/* ------------------------------------------------------------------ */
/*  PRELOADER                                                          */
/* ------------------------------------------------------------------ */

function Preloader({ onDone }) {
  const [progress, setProgress] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const tick = window.setInterval(() => {
      setProgress((p) => {
        if (p >= 100) return 100;
        const step = p < 40 ? 3 : p < 75 ? 2 : 1.2;
        return Math.min(100, p + step);
      });
    }, 40);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    if (progress < 100) return;
    const exitTimer = window.setTimeout(() => setExiting(true), 350);
    const doneTimer = window.setTimeout(() => onDone?.(), 1100);
    return () => {
      window.clearTimeout(exitTimer);
      window.clearTimeout(doneTimer);
    };
  }, [progress, onDone]);

  return (
    <Box
      aria-hidden={exiting}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        bgcolor: "#100e0b",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        opacity: exiting ? 0 : 1,
        transform: exiting ? "scale(1.04)" : "scale(1)",
        transition: "opacity 700ms ease, transform 700ms ease",
        pointerEvents: exiting ? "none" : "auto",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(600px 400px at 25% 80%, rgba(215,166,109,.09), transparent 60%),
            radial-gradient(500px 360px at 80% 20%, rgba(215,166,109,.07), transparent 60%)
          `,
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: { xs: 140, md: 180 },
          height: { xs: 140, md: 180 },
          display: "grid",
          placeItems: "center",
          mb: 5,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 200 200"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            animation: "spin 6s linear infinite",
            "@keyframes spin": { to: { transform: "rotate(360deg)" } },
          }}
        >
          <defs>
            <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={ACCENT} stopOpacity="1" />
              <stop offset="60%" stopColor={ACCENT} stopOpacity=".15" />
              <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle
            cx="100"
            cy="100"
            r="88"
            fill="none"
            stroke="url(#ringGrad)"
            strokeWidth="1.5"
            strokeDasharray="260 340"
            strokeLinecap="round"
          />
        </Box>
        <Box
          component="svg"
          viewBox="0 0 200 200"
          sx={{
            position: "absolute",
            inset: 12,
            width: "calc(100% - 24px)",
            height: "calc(100% - 24px)",
            animation: "spinReverse 9s linear infinite",
            "@keyframes spinReverse": { to: { transform: "rotate(-360deg)" } },
          }}
        >
          <circle
            cx="100"
            cy="100"
            r="88"
            fill="none"
            stroke="rgba(215,166,109,.25)"
            strokeWidth="0.8"
            strokeDasharray="2 6"
          />
        </Box>

        {/* Logo inside the ring */}
        <Box
          sx={{
            width: { xs: 86, md: 108 },
            height: { xs: 86, md: 108 },
            borderRadius: "50%",
            overflow: "hidden",
            border: `1px solid ${ACCENT}`,
            boxShadow: "0 8px 32px rgba(0,0,0,.55)",
            animation: "pulse 2.4s ease-in-out infinite",
            "@keyframes pulse": {
              "0%,100%": { opacity: 0.9, transform: "scale(1)" },
              "50%": { opacity: 1, transform: "scale(1.03)" },
            },
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Terrace Lounge logo"
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: { xs: "1.35rem", md: "1.7rem" },
          letterSpacing: "-.03em",
          color: "#fff",
          mb: 0.8,
        }}
      >
        TERRACE<span style={{ color: ACCENT }}>.</span>
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: ".55rem", md: ".62rem" },
          letterSpacing: ".3em",
          color: "#c9bfae",
          mb: 5,
        }}
      >
        L O U N G E · A B U J A
      </Typography>

      <Box sx={{ width: { xs: 200, md: 260 }, position: "relative" }}>
        <Box
          sx={{
            height: 2,
            width: "100%",
            bgcolor: "rgba(255,255,255,.1)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${ACCENT}, #f0c58f)`,
              transition: "width 200ms ease",
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: ".58rem",
              letterSpacing: ".22em",
              color: "#8d867d",
            }}
          >
            PREPARING YOUR TABLE
          </Typography>
          <Typography
            sx={{
              fontSize: ".62rem",
              color: ACCENT,
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {Math.round(progress)}%
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: { xs: 28, md: 40 },
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 6,
            whiteSpace: "nowrap",
            animation: "marquee 18s linear infinite",
            "@keyframes marquee": {
              "0%": { transform: "translateX(0)" },
              "100%": { transform: "translateX(-50%)" },
            },
          }}
        >
          {[0, 1].map((k) => (
            <Box key={k} sx={{ display: "flex", gap: 6 }}>
              {["SIGNATURE POURS", "LATE NIGHTS", "GOOD COMPANY", "CRAFT COCKTAILS", "ABUJA AFTER DARK"].map(
                (word) => (
                  <Typography
                    key={`${k}-${word}`}
                    sx={{
                      fontSize: ".62rem",
                      letterSpacing: ".28em",
                      color: "rgba(215,166,109,.55)",
                    }}
                  >
                    {word} <span style={{ color: ACCENT }}>·</span>
                  </Typography>
                )
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  FOOTER                                                             */
/* ------------------------------------------------------------------ */

function InfoRow({ icon, label, children, href }) {
  const content = (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          borderRadius: "6px",
          background: "rgba(215,166,109,.1)",
          border: "1px solid rgba(215,166,109,.28)",
          color: ACCENT,
          flexShrink: 0,
          mt: 0.3,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: ".55rem",
            letterSpacing: ".18em",
            color: MUTED,
            lineHeight: 1,
            mb: 0.7,
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: ".85rem",
            color: "#f6f1e9",
            lineHeight: 1.5,
            fontWeight: 500,
            wordBreak: "break-word",
          }}
        >
          {children}
        </Typography>
      </Box>
    </Box>
  );

  if (!href) return content;
  return (
    <Box
      component="a"
      href={href}
      sx={{
        textDecoration: "none",
        display: "block",
        "&:hover .MuiTypography-root": { color: ACCENT },
      }}
    >
      {content}
    </Box>
  );
}

function Footer({ restaurantInfo = {} }) {
  const info = {
    ...CONTACT,
    ...restaurantInfo,
    socials: { ...CONTACT.socials, ...(restaurantInfo.socials || {}) },
  };

  const hours = normaliseHours(restaurantInfo.hours);
  const telHref =
    info.phoneHref || `tel:${String(info.phone || "").replace(/[^\d+]/g, "")}`;

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        bgcolor: "#151310",
        color: "#f7f3ed",
        borderTop: `1px solid ${HAIRLINE}`,
        pt: { xs: 6, md: 8 },
        pb: 4,
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `
            radial-gradient(600px 300px at 10% 100%, rgba(215,166,109,.06), transparent 60%),
            radial-gradient(500px 260px at 90% 0%, rgba(215,166,109,.05), transparent 60%)
          `,
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1.1fr 1fr 1fr" },
            gap: { xs: 4, md: 5 },
          }}
        >
          {/* Brand + contact */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.6, mb: 1 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: `1px solid rgba(215,166,109,.45)`,
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="Terrace Lounge logo"
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: { xs: "1.6rem", md: "1.9rem" },
                  letterSpacing: "-.04em",
                  lineHeight: 1,
                  color: "#fff",
                }}
              >
                TERRACE<span style={{ color: ACCENT }}>.</span>
              </Typography>
            </Box>
            <Typography
              sx={{
                mt: 0.6,
                fontSize: ".6rem",
                letterSpacing: ".25em",
                color: "#d2c7b8",
                mb: 3,
              }}
            >
              L O U N G E · A B U J A
            </Typography>

            <Box sx={{ display: "grid", gap: 2.2 }}>
              <InfoRow icon={<LocationOn sx={{ fontSize: 16 }} />} label="ADDRESS">
                {info.address}
              </InfoRow>
              <InfoRow icon={<PhoneIcon sx={{ fontSize: 16 }} />} label="CALL US" href={telHref}>
                {info.phone}
              </InfoRow>
              <InfoRow icon={<Email sx={{ fontSize: 16 }} />} label="EMAIL" href={`mailto:${info.email}`}>
                {info.email}
              </InfoRow>
            </Box>
          </Box>

          {/* Opening hours */}
          <Box>
            <Typography
              sx={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: { xs: "1.5rem", md: "1.7rem" },
                letterSpacing: "-.03em",
                lineHeight: 1.1,
                mb: 3,
              }}
            >
              Opening hours
            </Typography>

            <Box
              sx={{
                border: `1px solid ${HAIRLINE}`,
                bgcolor: "rgba(255,255,255,.02)",
                p: 2.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Schedule sx={{ fontSize: 18, color: ACCENT }} />
                <Typography
                  sx={{
                    fontSize: ".58rem",
                    letterSpacing: ".18em",
                    color: MUTED,
                    fontWeight: 600,
                  }}
                >
                  WEEKLY SCHEDULE
                </Typography>
              </Box>

              {hours.map((h, i) => (
                <Box
                  key={`${h.label}-${i}`}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 2,
                    py: 1.4,
                    borderTop: i === 0 ? "none" : `1px solid ${HAIRLINE}`,
                  }}
                >
                  <Typography sx={{ fontSize: ".82rem", color: "#e7e1d8", fontWeight: 500 }}>
                    {h.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: ".82rem",
                      color: ACCENT,
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Follow + credit */}
          <Box>
            <Typography
              sx={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: { xs: "1.5rem", md: "1.7rem" },
                letterSpacing: "-.03em",
                lineHeight: 1.1,
                mb: 0.6,
              }}
            >
              Follow along
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: ".8rem", mb: 3 }}>
              Nights, plates and behind-the-bar moments.
            </Typography>

            <Box sx={{ display: "flex", gap: 1.2, mb: 4 }}>
              {Object.entries(socialMeta).map(([key, meta]) => {
                const href = info.socials?.[key];
                if (!href) return null;
                return (
                  <IconButton
                    key={key}
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={meta.label}
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "8px",
                      color: ACCENT,
                      border: "1px solid rgba(215,166,109,.35)",
                      background: "rgba(215,166,109,.06)",
                      transition: "all .25s ease",
                      "&:hover": {
                        background: ACCENT,
                        color: "#151310",
                        borderColor: ACCENT,
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 22px rgba(215,166,109,.25)",
                      },
                    }}
                  >
                    {meta.icon}
                  </IconButton>
                );
              })}
            </Box>

            <Box
              sx={{
                borderTop: `1px solid ${HAIRLINE}`,
                pt: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontSize: ".58rem",
                  letterSpacing: ".18em",
                  color: MUTED,
                  fontWeight: 600,
                }}
              >
                Designed by: Eleganté IT dept
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: { xs: 4, md: 5 }, borderColor: HAIRLINE }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Typography sx={{ color: "#8d867d", fontSize: ".78rem" }}>
            © {new Date().getFullYear()} The Terrace Lounge. All rights reserved.
          </Typography>

          <Box sx={{ display: "flex", gap: 3 }}>
            {["Privacy Policy", "Terms of Service"].map((label) => (
              <Typography
                key={label}
                component="a"
                href="#"
                sx={{
                  color: "#8d867d",
                  fontSize: ".78rem",
                  textDecoration: "none",
                  transition: "color .25s ease",
                  "&:hover": { color: ACCENT },
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Container>

      <Box
        aria-hidden
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`,
          opacity: 0.7,
        }}
      />
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  MAIN CONTENT                                                       */
/* ------------------------------------------------------------------ */

export default function MainContent({ menuData = [], qrMenuURL, restaurantInfo = {} }) {
  const [loading, setLoading] = useState(true);

  // CONTACT values win so stale backend data can't override them.
  const contact = {
    phone: CONTACT.phone,
    phoneHref: CONTACT.phoneHref,
    address: CONTACT.address,
    email: CONTACT.email,
    hours: CONTACT.hours,
  };

  const resolvedQrMenuURL = qrMenuURL || (typeof window !== "undefined" ? window.location.href : "");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const qrRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [search, setSearch] = useState("");

  const categories = useMemo(() => cleanMenuData(menuData), [menuData]);

  useEffect(() => {
    const timer = window.setInterval(
      () => setActiveSlide((current) => (current + 1) % slides.length),
      6500
    );
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (loading) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [loading]);

  const visibleCategories = useMemo(() => {
    const query = normalise(search);
    if (!query) return categories;

    const categoryMatches = (category) => normalise(category.name).includes(query);
    const subMatches = (sub) => normalise(sub.name).includes(query);
    const itemMatches = (item) =>
      `${item.name || ""} ${item.description || ""}`.toLowerCase().includes(query);

    return categories
      .map((category) => {
        const catHit = categoryMatches(category);

        const subcategories = category.subcategories
          .map((sub) => {
            const subHit = catHit || subMatches(sub);
            const items = subHit ? sub.items : sub.items.filter(itemMatches);
            return { ...sub, items };
          })
          .filter((sub) => sub.items.length || catHit || subMatches(sub));

        const items = catHit ? category.items : category.items.filter(itemMatches);

        return { ...category, items, subcategories, _catHit: catHit };
      })
      .filter((category) => category._catHit || category.items.length || category.subcategories.length)
      .map(({ _catHit, ...rest }) => rest);
  }, [categories, search]);

  useEffect(() => {
    if (!normalise(search)) return;
    const categoryState = {};
    const subcategoryState = {};
    visibleCategories.forEach((category, index) => {
      const categoryKey = category.id ?? index;
      categoryState[categoryKey] = true;
      category.subcategories.forEach((sub, subIndex) => {
        subcategoryState[sub.id ?? `${categoryKey}-${subIndex}`] = true;
      });
    });
    setExpandedCategories((current) => ({ ...current, ...categoryState }));
    setExpandedSubcategories((current) => ({ ...current, ...subcategoryState }));
  }, [search, visibleCategories]);

  const scrollToMenu = () => {
    document.getElementById("menu-section")?.scrollIntoView({ behavior: "smooth" });
    setMobileNavOpen(false);
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      "Hello! I'd like to make a reservation at The Terrace Lounge."
    );
    window.open(`https://wa.me/2347041005087?text=${message}`, "_blank", "noopener,noreferrer");
  };

  const toggleAll = () => {
    const next = !allExpanded;
    const categoryState = {};
    const subcategoryState = {};
    if (next) {
      visibleCategories.forEach((category, index) => {
        categoryState[category.id ?? index] = true;
        category.subcategories.forEach((sub, subIndex) => {
          subcategoryState[sub.id ?? `${category.id ?? index}-${subIndex}`] = true;
        });
      });
    }
    setExpandedCategories(categoryState);
    setExpandedSubcategories(subcategoryState);
    setAllExpanded(next);
  };

  const downloadQr = async () => {
    if (!qrRef.current) return;
    try {
      const canvas = await html2canvas(qrRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
      });
      const link = document.createElement("a");
      link.download = "terrace-lounge-menu-qr.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error("Could not export QR code:", error);
    }
  };

  const renderItems = (items = []) => (
    <List disablePadding>
      {items.map((item, index) => (
        <React.Fragment key={item.id ?? `${item.name}-${index}`}>
          <ListItem alignItems="flex-start" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
            <ListItemText
              primary={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography sx={{ color: "#f6f1e9", fontWeight: 600, lineHeight: 1.4 }}>
                    {item.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: ACCENT,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatPrice(item.price)}
                  </Typography>
                </Box>
              }
              secondary={
                item.description ? (
                  <Typography component="span" sx={{ display: "block", mt: 0.6, color: MUTED, lineHeight: 1.6 }}>
                    {item.description}
                  </Typography>
                ) : null
              }
            />
          </ListItem>
          {index < items.length - 1 && <Divider sx={{ borderColor: "rgba(255,255,255,.08)", mx: 3 }} />}
        </React.Fragment>
      ))}
    </List>
  );

  return (
    <>
      {loading && <Preloader onDone={() => setLoading(false)} />}

      <Box
        sx={{
          bgcolor: INK,
          color: "#f7f3ed",
          minHeight: "100vh",
          overflow: "hidden",
          opacity: loading ? 0 : 1,
          transition: "opacity 700ms ease",
        }}
      >
        {/* HEADER */}
        <AppBar
          position="absolute"
          elevation={0}
          sx={{
            bgcolor: "rgba(18,17,15,.94)",
            backdropFilter: "blur(16px)",
            borderBottom: "1px solid rgba(255,255,255,.12)",
            zIndex: 1100,
          }}
        >
          <Container maxWidth="xl">
            <Toolbar
              disableGutters
              sx={{
                minHeight: { xs: 64, md: 88 },
                gap: { xs: 1, md: 2.5 },
                flexWrap: "nowrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, flexGrow: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    width: { xs: 36, md: 44 },
                    height: { xs: 36, md: 44 },
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: "1px solid rgba(215,166,109,.45)",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="Terrace Lounge logo"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: "Georgia, 'Times New Roman', serif",
                      fontSize: { xs: "1.25rem", md: "1.65rem" },
                      letterSpacing: "-.04em",
                      lineHeight: 1,
                      color: "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    TERRACE<span style={{ color: ACCENT }}>.</span>
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.7,
                      fontSize: { xs: ".5rem", md: ".58rem" },
                      letterSpacing: { xs: ".18em", md: ".25em" },
                      color: "#d2c7b8",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    L O U N G E · A B U J A
                  </Typography>
                </Box>
              </Box>

              {!isMobile && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                  <Box
                    component="a"
                    href={`tel:${contact.phoneHref}`}
                    sx={{ display: "flex", alignItems: "center", gap: 1, textDecoration: "none" }}
                  >
                    <Box sx={infoChipSx}>
                      <PhoneIcon sx={{ fontSize: 17, color: ACCENT }} />
                    </Box>
                    <Box>
                      <Typography sx={infoLabelSx}>CALL US</Typography>
                      <Typography sx={{ fontSize: ".82rem", fontWeight: 600, color: "#f6f1e9", lineHeight: 1.4, whiteSpace: "nowrap" }}>
                        {contact.phone}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={infoChipSx}>
                      <LocationIcon sx={{ fontSize: 17, color: ACCENT }} />
                    </Box>
                    <Box sx={{ maxWidth: 180 }}>
                      <Typography sx={infoLabelSx}>ADDRESS</Typography>
                      <Typography
                        sx={{
                          fontSize: ".78rem",
                          fontWeight: 600,
                          color: "#f6f1e9",
                          lineHeight: 1.3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {contact.address}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={infoChipSx}>
                      <AccessTimeIcon sx={{ fontSize: 17, color: ACCENT }} />
                    </Box>
                    <Box>
                      <Typography sx={infoLabelSx}>OPENING HOURS</Typography>
                      {contact.hours.map((h) => (
                        <Typography
                          key={h.label}
                          sx={{ fontSize: ".72rem", fontWeight: 600, color: "#f6f1e9", lineHeight: 1.3, whiteSpace: "nowrap" }}
                        >
                          {h.label}: {h.value}
                        </Typography>
                      ))}
                    </Box>
                  </Box>

                  <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,.15)", my: 1 }} />

                  <Button
                    onClick={scrollToMenu}
                    sx={{
                      color: "#eee8df",
                      textTransform: "none",
                      fontSize: ".85rem",
                      fontWeight: 500,
                      minWidth: 0,
                      "&:hover": { color: ACCENT, bgcolor: "transparent" },
                    }}
                  >
                    Our Menu
                  </Button>

                  <Button
                    component="a"
                    href="https://myelegante.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<ArrowOutward sx={{ fontSize: 16 }} />}
                    sx={{
                      color: "#eee8df",
                      textTransform: "none",
                      fontSize: ".85rem",
                      fontWeight: 500,
                      minWidth: 0,
                      "&:hover": { color: ACCENT, bgcolor: "transparent" },
                    }}
                  >
                    Elegante&apos;
                  </Button>

                  <Button
                    onClick={openWhatsApp}
                    variant="outlined"
                    endIcon={<ArrowOutward />}
                    sx={{
                      px: 2.2,
                      py: 1.1,
                      borderRadius: 0,
                      borderColor: ACCENT,
                      color: "#f8e9d4",
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      "&:hover": { borderColor: "#f0c58f", bgcolor: "rgba(215,166,109,.1)" },
                    }}
                  >
                    Book a table
                  </Button>
                </Box>
              )}

              {isMobile && (
                <IconButton
                  aria-label="Toggle navigation"
                  onClick={() => setMobileNavOpen((open) => !open)}
                  sx={{ color: "white", flexShrink: 0 }}
                >
                  {mobileNavOpen ? <Close /> : <MenuIcon />}
                </IconButton>
              )}
            </Toolbar>
          </Container>

          {isMobile && (
            <Box
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                bgcolor: "rgba(18,17,15,.98)",
                backdropFilter: "blur(16px)",
                borderBottom: "1px solid rgba(255,255,255,.12)",
                boxShadow: "0 12px 32px rgba(0,0,0,.45)",
                px: 2,
                py: mobileNavOpen ? 2 : 0,
                maxHeight: mobileNavOpen ? "calc(100vh - 64px)" : 0,
                overflowY: "auto",
                opacity: mobileNavOpen ? 1 : 0,
                visibility: mobileNavOpen ? "visible" : "hidden",
                transition: "opacity 220ms ease, max-height 260ms ease, padding 220ms ease",
                zIndex: 1200,
              }}
            >
              <Box sx={{ display: "grid", gap: 0.5 }}>
                <Button
                  onClick={() => { window.scrollTo({ top: 0, behavior: "smooth" }); setMobileNavOpen(false); }}
                  sx={{ color: "white", justifyContent: "flex-start", textTransform: "none", py: 1.2 }}
                >
                  Home
                </Button>
                <Button
                  onClick={scrollToMenu}
                  sx={{ color: "white", justifyContent: "flex-start", textTransform: "none", py: 1.2 }}
                >
                  Our Menu
                </Button>
                <Button
                  component="a"
                  href="https://myelegante.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "white", justifyContent: "flex-start", textTransform: "none", py: 1.2 }}
                >
                  Elegante&apos; <ArrowOutward sx={{ ml: 1, fontSize: 16 }} />
                </Button>
                <Button
                  onClick={openWhatsApp}
                  sx={{ color: ACCENT, justifyContent: "flex-start", textTransform: "none", py: 1.2 }}
                >
                  Book a table <ArrowOutward sx={{ ml: 1, fontSize: 16 }} />
                </Button>
              </Box>

              <Divider sx={{ borderColor: "rgba(255,255,255,.12)", my: 1.5 }} />

              <Box
                component="a"
                href={`tel:${contact.phoneHref}`}
                sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, textDecoration: "none" }}
              >
                <Box sx={infoChipSx}>
                  <PhoneIcon sx={{ fontSize: 17, color: ACCENT }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: ".55rem", letterSpacing: ".15em", color: "#a9a39a" }}>CALL US</Typography>
                  <Typography sx={{ fontSize: ".88rem", fontWeight: 600, color: "#f6f1e9", wordBreak: "break-word" }}>
                    {contact.phone}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1 }}>
                <Box sx={infoChipSx}>
                  <LocationIcon sx={{ fontSize: 17, color: ACCENT }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: ".55rem", letterSpacing: ".15em", color: "#a9a39a" }}>ADDRESS</Typography>
                  <Typography sx={{ fontSize: ".88rem", fontWeight: 600, color: "#f6f1e9", lineHeight: 1.4, wordBreak: "break-word" }}>
                    {contact.address}
                  </Typography>
                </Box>
              </Box>

              <Box
                component="a"
                href={`mailto:${contact.email}`}
                sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, textDecoration: "none" }}
              >
                <Box sx={infoChipSx}>
                  <MailOutlineIcon sx={{ fontSize: 17, color: ACCENT }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: ".55rem", letterSpacing: ".15em", color: "#a9a39a" }}>EMAIL</Typography>
                  <Typography sx={{ fontSize: ".88rem", fontWeight: 600, color: "#f6f1e9", wordBreak: "break-word" }}>
                    {contact.email}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1 }}>
                <Box sx={infoChipSx}>
                  <AccessTimeIcon sx={{ fontSize: 17, color: ACCENT }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: ".55rem", letterSpacing: ".15em", color: "#a9a39a" }}>OPENING HOURS</Typography>
                  {contact.hours.map((h) => (
                    <Typography key={h.label} sx={{ fontSize: ".84rem", fontWeight: 600, color: "#f6f1e9", lineHeight: 1.4 }}>
                      {h.label}: {h.value}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </AppBar>

        {/* HERO */}
        <Box sx={{ position: "relative", height: { xs: "86vh", md: "92vh" }, minHeight: 640, bgcolor: "#211b15" }}>
          {slides.map((slide, index) => (
            <Box
              key={slide.image}
              aria-hidden={index !== activeSlide}
              sx={{
                position: "absolute",
                inset: 0,
                opacity: index === activeSlide ? 1 : 0,
                transition: "opacity 900ms ease",
                backgroundImage: `linear-gradient(90deg, rgba(12,11,9,.88) 0%, rgba(12,11,9,.58) 46%, rgba(12,11,9,.12) 100%), linear-gradient(0deg, rgba(12,11,9,.62), transparent 55%), url("${slide.image}")`,
                backgroundPosition: "center",
                backgroundSize: "cover",
              }}
            />
          ))}

          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1, height: "100%", display: "flex", alignItems: "center", pt: { xs: 10, md: 12 } }}>
            <Box sx={{ maxWidth: 620, py: 8 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Box sx={{ width: 32, height: 1, bgcolor: ACCENT }} />
                <Typography sx={{ color: ACCENT, letterSpacing: ".22em", fontSize: { xs: ".6rem", md: ".66rem" }, fontWeight: 700 }}>
                  {slides[activeSlide].eyebrow}
                </Typography>
              </Box>
              <Typography
                component="h1"
                sx={{
                  whiteSpace: "pre-line",
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: { xs: "2rem", sm: "2.6rem", md: "3.6rem" },
                  lineHeight: { xs: 1.1, md: 1.05 },
                  letterSpacing: "-.04em",
                  fontWeight: 400,
                  maxWidth: 560,
                  textWrap: "balance",
                }}
              >
                {slides[activeSlide].title}
              </Typography>
              <Typography sx={{ mt: 2, maxWidth: 420, color: "#ded7ce", fontSize: { xs: ".88rem", md: ".98rem" }, lineHeight: 1.7 }}>
                {slides[activeSlide].subtitle}
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 3.5 }}>
                <Button
                  onClick={scrollToMenu}
                  endIcon={<ArrowDownward />}
                  sx={{
                    bgcolor: ACCENT,
                    color: "#17120d",
                    borderRadius: 0,
                    px: 3,
                    py: 1.4,
                    fontSize: ".85rem",
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#edc18b" },
                  }}
                >
                  Explore the menu
                </Button>
                <Button
                  onClick={openWhatsApp}
                  startIcon={<WhatsApp />}
                  sx={{
                    border: "1px solid rgba(255,255,255,.45)",
                    color: "#fff",
                    borderRadius: 0,
                    px: 2.8,
                    py: 1.4,
                    fontSize: ".85rem",
                    textTransform: "none",
                    "&:hover": { borderColor: ACCENT, bgcolor: "rgba(255,255,255,.06)" },
                  }}
                >
                  Reserve a table
                </Button>
              </Box>
            </Box>
          </Container>

          <Box sx={{ position: "absolute", zIndex: 2, right: { xs: 2, md: 5 }, bottom: 36, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: "#fff", fontSize: ".75rem", mr: 1, fontVariantNumeric: "tabular-nums" }}>
              0{activeSlide + 1} <span style={{ color: "#92877a" }}>/ 0{slides.length}</span>
            </Typography>
            <IconButton aria-label="Previous slide" onClick={() => setActiveSlide((activeSlide - 1 + slides.length) % slides.length)} sx={{ border: "1px solid rgba(255,255,255,.35)", color: "white", borderRadius: 0 }}>
              <ChevronLeft />
            </IconButton>
            <IconButton aria-label="Next slide" onClick={() => setActiveSlide((activeSlide + 1) % slides.length)} sx={{ border: "1px solid rgba(255,255,255,.35)", color: "white", borderRadius: 0 }}>
              <ChevronRight />
            </IconButton>
          </Box>
          <Box sx={{ position: "absolute", bottom: 0, left: 0, width: `${((activeSlide + 1) / slides.length) * 100}%`, height: 3, bgcolor: ACCENT, transition: "width 300ms ease" }} />
        </Box>

        {/* MENU */}
        <Box id="menu-section" sx={{ bgcolor: "#211e19", py: { xs: 7, md: 10 }, borderTop: "1px solid rgba(255,255,255,.07)" }}>
          <Container maxWidth="xl">
            <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "end" }, justifyContent: "space-between", gap: 3, flexWrap: "wrap", mb: 5 }}>
              <Box>
                <Typography sx={{ color: ACCENT, letterSpacing: ".2em", fontSize: ".72rem", fontWeight: 700, mb: 1.5 }}>MADE TO BE SAVOURED</Typography>
                <Typography component="h2" sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: { xs: "2.7rem", md: "4rem" }, letterSpacing: "-.04em", lineHeight: 1.05, fontWeight: 400 }}>
                  The menu
                </Typography>
                <Typography sx={{ color: MUTED, mt: 1.5 }}>Good ingredients. Thoughtful plates. Something for every mood.</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap", alignItems: "center" }}>
                <Box
                  component="input"
                  aria-label="Search menu"
                  placeholder="Search dishes or categories..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  sx={{
                    width: { xs: "100%", sm: 250 },
                    bgcolor: "transparent",
                    border: "1px solid rgba(255,255,255,.22)",
                    borderRadius: 0,
                    color: "white",
                    px: 2,
                    py: 1.3,
                    font: "inherit",
                    outline: "none",
                    "&:focus": { borderColor: ACCENT },
                    "&::placeholder": { color: "#aaa197" },
                  }}
                />
                <Button onClick={toggleAll} sx={{ color: ACCENT, border: "1px solid rgba(215,166,109,.55)", borderRadius: 0, px: 2, py: 1.25, textTransform: "none" }}>
                  {allExpanded ? "Collapse all" : "Expand all"}
                </Button>
              </Box>
            </Box>

            {visibleCategories.length === 0 ? (
              <Paper sx={{ p: 4, bgcolor: "#29251f", color: MUTED, borderRadius: 0 }}>
                {categories.length ? "No dishes match your search." : "Menu items will appear here when available."}
              </Paper>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 340px" }, gap: 4, alignItems: "start" }}>
                <Box>
                  {visibleCategories.map((category, index) => {
                    const categoryKey = category.id ?? index;
                    const categoryOpen = Boolean(expandedCategories[categoryKey]);
                    const sectionCount = category.subcategories.length + (category.items.length ? 1 : 0);

                    return (
                      <Paper key={categoryKey} elevation={0} sx={{ mb: 1.5, bgcolor: "#29251f", border: "1px solid rgba(255,255,255,.08)", borderRadius: 0, overflow: "hidden" }}>
                        <Box
                          role="button"
                          aria-expanded={categoryOpen}
                          tabIndex={0}
                          onClick={() => setExpandedCategories((state) => ({ ...state, [categoryKey]: !state[categoryKey] }))}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setExpandedCategories((state) => ({ ...state, [categoryKey]: !state[categoryKey] }));
                            }
                          }}
                          sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: 2, md: 2.5 }, cursor: "pointer", "&:hover": { bgcolor: "rgba(255,255,255,.025)" } }}
                        >
                          <Box sx={{ width: 48, height: 48, display: "grid", placeItems: "center", border: "1px solid rgba(215,166,109,.45)", color: ACCENT, flexShrink: 0 }}>
                            {getCategoryIcon(category.name)}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: { xs: "1.3rem", md: "1.55rem" }, color: "#f6f1e9" }}>{category.name}</Typography>
                            <Typography sx={{ color: MUTED, fontSize: ".78rem", mt: 0.3 }}>{sectionCount} {sectionCount === 1 ? "section" : "sections"}</Typography>
                          </Box>
                          {categoryOpen ? <ExpandLess sx={{ color: ACCENT }} /> : <ExpandMore sx={{ color: ACCENT }} />}
                        </Box>
                        <Collapse in={categoryOpen} unmountOnExit>
                          <Divider sx={{ borderColor: "rgba(255,255,255,.08)" }} />
                          <Box sx={{ p: { xs: 1, sm: 2 } }}>
                            {category.subcategories.map((sub, subIndex) => {
                              const subKey = sub.id ?? `${categoryKey}-${subIndex}`;
                              const subOpen = Boolean(expandedSubcategories[subKey]);
                              return (
                                <Card key={subKey} elevation={0} sx={{ mb: 1.5, bgcolor: "#211e19", border: "1px solid rgba(255,255,255,.07)", borderRadius: 0, color: "white" }}>
                                  <Box
                                    role="button"
                                    aria-expanded={subOpen}
                                    tabIndex={0}
                                    onClick={() => setExpandedSubcategories((state) => ({ ...state, [subKey]: !state[subKey] }))}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        setExpandedSubcategories((state) => ({ ...state, [subKey]: !state[subKey] }));
                                      }
                                    }}
                                    sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, cursor: "pointer" }}
                                  >
                                    <Box sx={{ width: 38, height: 38, display: "grid", placeItems: "center", color: ACCENT, border: "1px solid rgba(215,166,109,.28)", flexShrink: 0 }}>
                                      {getSubcategoryIcon(sub.name)}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography sx={{ fontWeight: 600 }}>{sub.name}</Typography>
                                      <Typography sx={{ color: MUTED, fontSize: ".78rem", mt: 0.4 }}>{sub.items.length} items</Typography>
                                    </Box>
                                    {subOpen ? <ExpandLess sx={{ color: ACCENT }} /> : <ExpandMore sx={{ color: ACCENT }} />}
                                  </Box>
                                  <Collapse in={subOpen} unmountOnExit>
                                    <Divider sx={{ borderColor: "rgba(255,255,255,.08)" }} />
                                    {renderItems(sub.items)}
                                  </Collapse>
                                </Card>
                              );
                            })}
                            {category.items.length > 0 && (
                              <Box sx={{ bgcolor: "#211e19", border: "1px solid rgba(255,255,255,.07)" }}>
                                {renderItems(category.items)}
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </Box>

                <Box sx={{ position: { lg: "sticky" }, top: 24 }}>
                  <Paper elevation={0} sx={{ bgcolor: "#f1e8db", color: INK, p: 3, borderRadius: 0 }}>
                    <Typography sx={{ color: "#80603d", letterSpacing: ".18em", fontSize: ".68rem", fontWeight: 800 }}>TAKE THE MENU WITH YOU</Typography>
                    <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "2rem", lineHeight: 1.1, mt: 1.5 }}>
                      A little taste<br />of Terrace.
                    </Typography>
                    <Typography sx={{ color: "#62584d", lineHeight: 1.7, mt: 1.5, fontSize: ".9rem" }}>
                      Scan to open our digital menu on your phone and share it with your table.
                    </Typography>
                    <Box ref={qrRef} sx={{ bgcolor: "#fff", p: 2, mt: 3, display: "inline-flex", maxWidth: "100%" }}>
                      <QRCodeCanvas value={resolvedQrMenuURL} size={190} fgColor="#211e19" bgColor="#ffffff" level="H" includeMargin />
                    </Box>
                    <Button fullWidth onClick={downloadQr} startIcon={<Share />} sx={{ mt: 2.5, py: 1.4, bgcolor: INK, color: "#fff", borderRadius: 0, textTransform: "none", "&:hover": { bgcolor: "#393027" } }}>
                      Download QR code
                    </Button>
                    <Button fullWidth onClick={openWhatsApp} startIcon={<WhatsApp />} sx={{ mt: 1, py: 1.4, color: INK, border: "1px solid rgba(23,21,18,.35)", borderRadius: 0, textTransform: "none", "&:hover": { bgcolor: "rgba(23,21,18,.05)" } }}>
                      Reserve on WhatsApp
                    </Button>
                  </Paper>
                 <Box
  sx={{
    mt: 2,
    minHeight: 320,
    backgroundImage: `linear-gradient(0deg, rgba(12,11,9,.7), rgba(12,11,9,.05)), url("${slide1}")`,
    backgroundPosition: "center",
    backgroundSize: "cover",
    display: "flex",
    alignItems: "end",
    p: 2.5,
  }}
>
                    <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.5rem" }}>
                      Come for the flavour.<br />Stay for the feeling.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Container>
        </Box>

        {/* FOOTER */}
        <Footer />
      </Box>
    </>
  );
}